import * as currency from 'currency.js';

import { CURRENCIES as isoCurrencies } from '../resources/iso_currencies';

export interface IIsoCurrencies {
  country: string;
  name: string;
  alphabetic_code: string;
  numeric_code: number;
  exponent: number;
}

const winstonKey = 'shared.helpers.currencyWrapper.abacusCurrency';
const defaultCurrencyCode = 'USD';
export const isoCurrenciesByCode: Record<string, IIsoCurrencies> = {};

isoCurrencies.forEach(function (isoCurrency) {
  if (isoCurrency.alphabetic_code) {
    isoCurrenciesByCode[isoCurrency.alphabetic_code] = isoCurrency;
  }
});

export class AbacusCurrency {
  currencyObject: currency;
  amountInMinorUnits: number;
  amountInMajorUnits: number;
  currency: IIsoCurrencies;
  constructor(amount: number | string | currency, currencyCode?: string, isAmountMajorUnits?: boolean, precision?: number) {
    if (!currencyCode) {
      currencyCode = defaultCurrencyCode;
    }

    if (!isoCurrenciesByCode[currencyCode]) {
      throw new Error('expected valid currency code');
    }

    this.currency = isoCurrenciesByCode[currencyCode];
    this.currencyObject = currency(amount, { fromCents: !isAmountMajorUnits, precision: precision || this.currency.exponent });
  }

  private getCurrencyObjectForAggregating(amount: currency.Any, currencyCode?: string, isAmountMajorUnits?: boolean) {
    const isUsingDefaultUSD = !currencyCode && this.currency.alphabetic_code === defaultCurrencyCode;

    // throw if the base currency code is not USD and currencyCode is either undefined, or provided currency code does not match the original currency code
    if (!isUsingDefaultUSD && currencyCode !== this.currency.alphabetic_code) {
      throw new Error(`${winstonKey}.addMinorAmount.CURRENCY_CODE_MUST_MATCH`);
    }
    return currency(amount, { fromCents: !isAmountMajorUnits, precision: this.currency.exponent });
  }
  getUnitValue = () => this.currencyObject.value;
  getMinorUnitValue = () => this.currencyObject.intValue;
  addMajorAmount(amount: currency.Any) {
    const currencyToAdd = this.getCurrencyObjectForAggregating(amount, this.currency.alphabetic_code, true);
    const newAmountObject = this.currencyObject.add(currencyToAdd);
    return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true);
  }
  subtractMajorAmount(amount: currency.Any) {
    const currencyToSubtract = this.getCurrencyObjectForAggregating(amount, this.currency.alphabetic_code, true);

    const newAmountObject = this.currencyObject.subtract(currencyToSubtract);
    return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true);
  }
  divide(divisor: number) {
    const newAmountObject = this.currencyObject.divide(divisor);

    return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true);
  }
  multiply(multiple: number) {
    const newAmountObject = this.currencyObject.multiply(multiple);

    return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true);
  }

  /**
   * Converts the instance currency object given the exchange rate and provided currency code.
   * Use caution with logic using inverted rates (e.g. from param currency code -> instance currency object)
   * @see convertAmountToTargetCurrency
   * @param exchangeRate
   * @param currencyCode
   * @returns
   */
  convert(exchangeRate: number, currencyCode: string) {
    const targetCurrency = isoCurrenciesByCode[currencyCode];
    const intermediatePrecision = Math.max(this.currency.exponent, targetCurrency?.exponent ?? 0) + 4;

    // Use an intermediate currency.js object with extra precision so source-currency exponent
    // does not truncate target fractional units before target normalization.
    const intermediateAmount = currency(this.currencyObject.value, {
      fromCents: false,
      precision: intermediatePrecision
    }).multiply(exchangeRate);

    return new AbacusCurrency(intermediateAmount.value, currencyCode, true);
  }

  split(numberOfSplits: number) {
    const splits = this.currencyObject.distribute(numberOfSplits);

    return splits.map((split) => new AbacusCurrency(split.value, this.currency.alphabetic_code, true));
  }
  toFormattedString(pruneZeroDecimals: boolean = false) {
    const formattedString = this.currencyObject.format();
    if (pruneZeroDecimals) {
      return formattedString.replace(/\.0+$/, '');
    }

    return formattedString;
  }
  // this will return the currency value in major units with decimal padding (no currency marker)
  getUnitStringValueWithDecimals() {
    return this.currencyObject.toString();
  }
  addMinorAmount = (amount: currency.Any, currencyCode?: string) =>
    this.addMajorAmount(this.getCurrencyObjectForAggregating(amount, currencyCode));
  subtractMinorAmount = (amount: currency.Any, currencyCode?: string) =>
    this.subtractMajorAmount(this.getCurrencyObjectForAggregating(amount, currencyCode));
}
